import "../styles/default.scss";

import autoBind from "auto-bind";
import classNames from "classnames";
import { isEmpty } from "lodash";
import React from "react";

import withDeviceType from "../../../../../hocs/withDeviceType";
import withMarketTransltaions from "../../../../../hocs/withMarketTranslations";
import withUniversalTable from "../../../../../hocs/withUniversalTable";
import accessLevelsMap from "../../../../../lib/access/accessLevelsMap";
import getAccessLevel from "../../../../../lib/access/getAccessLevel";
import UniversalTableContext from "../../../../../lib/UniversalTable/UniversalTableContext";
import Loader from "../../Loader";
import NoPermissionForPlan from "../../NoPermissionForPlan";
import NoAccessDisclaimer from "../../NoAccessDisclaimer";
import SquaberTable from "../../SquaberTable";
import ActiveFilters from "../components/ActiveFilters/ActiveFilters";
import LoadMore from "../components/LoadMore/LoadMore";
import Pagination from "../components/Pagination/Pagination";
import Settings from "../components/Settings/Settings";
import SettingsButton from "../components/SettingsButton/SettingsButton";
import Tabs from "../components/Tabs/Tabs";
import TrendStatusLegend from "../components/TrendStatusLegend/TrendStatusLegend";
import Renderer from "../renderers";
import ApiHelper from "../../../../../lib/UniversalTable/CoreHelpers/ApiHelper";
import { getUniversalTableLiteColumnCellProps } from "../columnMinWidths";
import { getStackedPairCellProps } from "../../../../../lib/UniversalTable/stackColumnPairCell";

@withUniversalTable
@withDeviceType
@withMarketTransltaions
class UniversalTableLiteWeb extends React.Component {
  static contextType = UniversalTableContext;

  constructor(props) {
    super(props);

    autoBind.react(this);
  }

  state = {
    settingsVisible: false
  };

  renderCell(result, cell, userHasAccess) {
    const { marketTranslations } = this.props;
    const { columnsFontSize } = this.context;

    return (
      <Renderer
        key={`${result.id}_${cell.attribute_name}`}
        blurred={!userHasAccess && cell.is_premium}
        value={result[cell.attribute_name]}
        type={cell.values_type}
        format={cell.values_format}
        columnFontSize={columnsFontSize[cell.attribute_name]}
        resultMetaData={result}
        attributeName={cell.attribute_name}
        rendererName={cell.renderer_name}
        label={cell.label}
        marketTranslations={marketTranslations}
        stackZoneEdges={Boolean(this.props.stackZoneEdges)}
      />
    );
  }

  getColumnCellProps(column) {
    return getUniversalTableLiteColumnCellProps(column, {
      fitColumns: Boolean(this.props.fitColumns),
      singleLineZoneParameters: !this.props.stackZoneEdges
    });
  }

  renderHeading(column) {
    return {
      content: (
        <Renderer
          key={`${column.attribute_name}_${column.label}`}
          value={column.label}
          type={"heading"}
          columnMetaData={column}
          attributeName={column.attribute_name}
        />
      ),
      ...this.getColumnCellProps(column)
    };
  }

  renderStackedPairHeading(pair) {
    const first = pair.csv_data_column_first;
    const second = pair.csv_data_column_second;
    const firstHeading = this.renderHeading(first);

    return {
      content: (
        <React.Fragment>
          <div className="column-pair-element">{firstHeading.content}</div>
          {second ? (
            <div className="column-pair-element">
              {this.renderHeading(second).content}
            </div>
          ) : null}
        </React.Fragment>
      ),
      ...getStackedPairCellProps(first, second)
    };
  }

  renderStackedPairCell(result, pair, userHasAccess) {
    const first = pair.csv_data_column_first;
    const second = pair.csv_data_column_second;

    return {
      content: (
        <React.Fragment>
          <div className="column-pair-element">
            {this.renderCell(result, first, userHasAccess)}
          </div>
          {second ? (
            <div className="column-pair-element">
              {this.renderCell(result, second, userHasAccess)}
            </div>
          ) : null}
        </React.Fragment>
      ),
      ...getStackedPairCellProps(first, second)
    };
  }

  showSettings() {
    this.setState({
      settingsVisible: true
    });
  }

  hideSettings() {
    this.setState({
      settingsVisible: false
    });
  }

  render() {
    const {
      userData,
      getColumns,
      getColumnPairs,
      fetchNextPage,
      setTab,
      usePagination,
      hideLoadMore,
      hasPermission,
      aboveTable,
      belowTable,
      emptyState,
      emptyStateWithFilters,
      locale,
      noAccessPermissions
    } = this.props;

    const { settingsVisible } = this.state;

    const {
      data,
      config,
      currentTab,
      currentTabId,
      dataLoading,
      configLoading,
      configAccessDenied,
      configAccessDeniedPermissions,
      configAccessDeniedReason,
      isFetchingNextPage
    } = this.context;

    const accessLevel = getAccessLevel(userData, {});
    const userHasAccess = accessLevel === accessLevelsMap.HAS_PREMIUM_ACCESS;

    if (configLoading) {
      return <Loader />;
    }

    if (configAccessDenied || !currentTab) {
      const { tabs } = config || {};
      const showTabs = tabs && tabs.length > 1;

      return (
        <div className="universal-table-lite">
          {aboveTable ? (
            <div className="universal-table-lite__header-main">
              {aboveTable}
            </div>
          ) : null}
          {showTabs ? (
            <Tabs tabs={tabs} currentTabId={currentTabId} setTab={setTab} />
          ) : null}
          <NoPermissionForPlan
            locale={locale}
            permissions={configAccessDeniedPermissions ?? noAccessPermissions}
            accessDeniedReason={configAccessDeniedReason}
          />
        </div>
      );
    }

    const userHasAccessToCurrentTab = !(
      !hasPermission(currentTab.permissions) && currentTab.is_premium
    );

    const shouldRenderColumnPairs = false;
    const stackColumnPairs = this.props.stackColumnPairs === true;
    const fitColumns = Boolean(this.props.fitColumns);

    const columns = getColumns();
    const columnPairs = getColumnPairs();
    const trendStatusColumn = ApiHelper.findTrendStatusColumn(this.context);

    const { tabs } = config;

    const hasActiveFilters =
      !isEmpty(this.context.search) || !isEmpty(this.context.filters);

    const hasNoResults =
      !dataLoading && data && (!data.results || data.results.length === 0);

    const emptyStateToShow = hasActiveFilters
      ? emptyStateWithFilters
      : emptyState;

    const shouldShowEmptyState = hasNoResults && emptyStateToShow;

    return (
      <div className="universal-table-lite">
        <div className="universal-table-lite__header">
          <div className="universal-table-lite__header-main">
            {userHasAccessToCurrentTab && aboveTable ? aboveTable : null}
          </div>
          <SettingsButton
            settingsVisible={settingsVisible}
            showSettings={this.showSettings}
            hideSettings={this.hideSettings}
          />
        </div>
        {tabs && tabs.length && tabs.length > 1 ? (
          <Tabs tabs={tabs} currentTabId={currentTabId} setTab={setTab} />
        ) : null}
        {userHasAccessToCurrentTab ? (
          <React.Fragment>
            <ActiveFilters />
            {shouldShowEmptyState ? (
              emptyStateToShow
            ) : (
              <React.Fragment>
                <div
                  className={classNames({
                    "table-wrapper": true,
                    "data-loading": dataLoading
                  })}
                >
                  <SquaberTable
                    withDistinctRows
                    className={
                      fitColumns ? "squaber-table--compact-columns" : undefined
                    }
                    heading={
                      stackColumnPairs
                        ? columnPairs.map(this.renderStackedPairHeading)
                        : shouldRenderColumnPairs && columnPairs
                          ? columnPairs.map(columnPair =>
                              [
                                columnPair.csv_data_column_first,
                                columnPair.csv_data_column_second
                              ]
                                .filter(column => !!column)
                                .map(this.renderHeading)
                            )
                          : columns.map(this.renderHeading)
                    }
                    rows={
                      columns && data && data.results
                        ? data.results.map(result =>
                            stackColumnPairs
                              ? columnPairs.map(pair =>
                                  this.renderStackedPairCell(
                                    result,
                                    pair,
                                    userHasAccess ||
                                      hasPermission(
                                        pair.csv_data_column_first.permissions
                                      )
                                  )
                                )
                              : shouldRenderColumnPairs
                                ? columnPairs.map(columnPair =>
                                    [
                                      columnPair.csv_data_column_first,
                                      columnPair.csv_data_column_second
                                    ]
                                      .filter(column => !!column)
                                      .map(column => ({
                                        content: this.renderCell(
                                          result,
                                          column,
                                          userHasAccess ||
                                            hasPermission(column.permissions)
                                        ),
                                        ...this.getColumnCellProps(column)
                                      }))
                                  )
                                : columns.map(column => ({
                                    content: this.renderCell(
                                      result,
                                      column,
                                      userHasAccess ||
                                        hasPermission(column.permissions)
                                    ),
                                    ...this.getColumnCellProps(column)
                                  }))
                          )
                        : []
                    }
                  />
                  {dataLoading ? (
                    <div className="table-loading-indicator">
                      <Loader small />
                    </div>
                  ) : null}
                </div>
                {usePagination ? (
                  <Pagination />
                ) : (
                  <React.Fragment>
                    {!hideLoadMore && data && data.next ? (
                      <LoadMore
                        isFetchingNextPage={isFetchingNextPage}
                        fetchNextPage={fetchNextPage}
                      />
                    ) : null}
                  </React.Fragment>
                )}
              </React.Fragment>
            )}
            {belowTable && !configAccessDenied ? (
              <div className="universal-table-lite__below-table">{belowTable}</div>
            ) : null}
            {trendStatusColumn ? (
              <TrendStatusLegend column={trendStatusColumn} />
            ) : null}
            {settingsVisible ? (
              <Settings hideSettings={this.hideSettings} />
            ) : null}
          </React.Fragment>
        ) : (
          <NoAccessDisclaimer />
        )}
      </div>
    );
  }
}

export default UniversalTableLiteWeb;
